import { useState } from 'react'
import { X } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { useCancelCarrierShipment } from '@/hooks/use-carrier-shipment-query'
import { CARRIER_SHIPMENT_STATE_UNKNOWN } from '@/lib/api/carrier-shipment-api'
import { ApiError } from '@/lib/api/client'
import { m } from '@/lib/paraglide/messages'

interface Props {
    orderId: string
}

export function CarrierCancelAction({ orderId }: Props) {
    const cancel = useCancelCarrierShipment(orderId)
    const [open, setOpen] = useState(false)
    const [note, setNote] = useState('')
    const [stateUnknown, setStateUnknown] = useState(false)

    const close = (nextOpen: boolean) => {
        setOpen(nextOpen)
        if (!nextOpen) {
            setStateUnknown(false)
            setNote('')
        }
    }

    const submit = async () => {
        if (stateUnknown) return
        try {
            await cancel.mutateAsync({ note })
            close(false)
        } catch (err) {
            if (
                err instanceof ApiError &&
                err.code === CARRIER_SHIPMENT_STATE_UNKNOWN
            ) {
                setStateUnknown(true)
                return
            }
            toast.error(
                err instanceof ApiError
                    ? err.message
                    : m.carrier_cancel_failed(),
            )
        }
    }

    return (
        <>
            <Button
                size="sm"
                variant="destructive"
                onClick={() => setOpen(true)}
            >
                <X className="size-3.5" />
                {m.carrier_cancel_shipment()}
            </Button>
            <Dialog open={open} onOpenChange={close}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{m.carrier_cancel_title()}</DialogTitle>
                        <DialogDescription>
                            {m.carrier_cancel_description()}
                        </DialogDescription>
                    </DialogHeader>
                    <Textarea
                        maxLength={500}
                        placeholder={m.carrier_cancel_note_placeholder()}
                        value={note}
                        onChange={(event) => setNote(event.target.value)}
                    />
                    {stateUnknown && (
                        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-2 text-sm">
                            <p className="text-destructive">
                                {m.carrier_state_unknown()}
                            </p>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => close(false)}>
                            {m.common_cancel()}
                        </Button>
                        {!stateUnknown && (
                            <Button
                                variant="destructive"
                                disabled={cancel.isPending}
                                onClick={() => void submit()}
                            >
                                {m.carrier_cancel_confirm()}
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    )
}
